import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { DocumentNode } from 'apollo-link';
import type { ComponentType } from 'react';
import React, { Fragment, useCallback, useRef, useState, useContext, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { SPACE_ARCHIVED_PAGES, CONTEXT_PATH } from '@confluence/named-routes';

import { fireTrackEvent, getActionName } from './AnalyticsEvents';
import { ArchiveProgressReport, getExperienceName } from './ArchiveProgressReport';
import { getArchiveFlagI18nWithErrorMsgs } from './getArchiveErrorMessage';

export type ArchiveDialogProps<TMutationVariables> = {
	archivingError?: Error;
	isArchiveInProgress: boolean;
	analyticsSource: string;
	isArchive: boolean;
	source: string;
	spaceAlias?: string | null;
	startMutationCallback(
		variables: TMutationVariables,
		trackEventAttributes?: object,
		shouldUseAutomationDiscovery?: boolean,
	): void;
};

export type ArchiveDialogPropsWithContentId<TMutationVariables> =
	ArchiveDialogProps<TMutationVariables> & {
		contentId: string;
	};

type ArchiveProcessContainerProps<TMutationVariables> = {
	contentId?: string;
	analyticsSource: string;
	isArchive: boolean;
	isBulk?: boolean;
	mutation: DocumentNode;
	onSuccess?(): void;
	onSuccessLinkClick?(): void;
	source: string;
	spaceAlias?: string | null;
	spaceId?: string | null;
	TriggerComponent: ComponentType<ArchiveDialogProps<TMutationVariables>>;
	pageCount: number;
	webuiLink?: string | null;
	featureName: string;
};

const MAX_TRIES = 3;

type MutationDataBase = {
	bulkArchivePages?: { taskId: string | null } | null;
	bulkUnarchivePages?: { taskId: string | null } | null;
	archivePages?: { taskId: string | null } | null;
};

export function ArchiveProcessContainer<TMutationVariables>({
	contentId,
	analyticsSource,
	isArchive,
	isBulk = false,
	mutation,
	onSuccess,
	source,
	spaceAlias,
	spaceId,
	TriggerComponent,
	onSuccessLinkClick,
	pageCount,
	webuiLink,
	featureName,
}: ArchiveProcessContainerProps<TMutationVariables>) {
	const [shouldShowProgress, setShouldShowProgress] = useState<boolean>(false);
	const [shouldUseAutomationDiscovery, setShouldUseAutomationDiscovery] = useState<boolean>(false);
	const [isArchiveInProgress, setIsArchiveInProgress] = useState<boolean>(false);
	const [longTaskId, setLongTaskId] = useState<string | undefined>(undefined);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// additionalTrackEventAttributes can be a ref because we just need to pass it on, we don't need
	// to re-render if it changes
	const additionalTrackEventAttributesRef = useRef<object | undefined>(undefined);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [mutationError, setMutationError] = useState<Error | undefined>(undefined);

	// MutationDataBase and TMutationVariables constitute casts from any. Their usage here adds type safety for
	// mutateStartPageArchival, but not for mutation. MutationDataBase is a static type defined in this file.
	// TMutationVariables is a generic type defined by the consumer of this component.
	// TODO: Improve type safety by refactoring code or by upgrading apollo to a version supporting TypedDocumentNode.
	const [mutateStartPageArchival] = useMutation<MutationDataBase, TMutationVariables>(mutation);

	const fireStartArchivalEvent = useCallback(
		(trackEventAttributes?: object) => {
			const actionName = getActionName(isArchive, isBulk);
			fireTrackEvent({
				createAnalyticsEvent,
				action: `${actionName}Started`,
				actionSubject: 'page',
				actionSubjectId: isBulk ? 'multiple' : contentId,
				source,
				attributes: {
					featureName,
					...trackEventAttributes,
				},
			});
			if (actionName === 'bulkArchive') {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'started',
						actionSubject: actionName,
						source: featureName,
						attributes: {
							pageCount,
						},
					},
				}).fire();
			}
		},
		[contentId, createAnalyticsEvent, isArchive, isBulk, source, featureName, pageCount],
	);

	const tryArchiveMutation = useCallback(
		(variables: TMutationVariables, waitTimeBeforeRetry = 3000, attemptNumber = 0) => {
			attemptNumber++;
			mutateStartPageArchival({ variables })
				.then((res) => {
					const data = res?.data;
					const nextLongTaskId =
						data?.bulkArchivePages?.taskId ||
						data?.bulkUnarchivePages?.taskId ||
						data?.archivePages?.taskId ||
						undefined;
					setLongTaskId(nextLongTaskId);
				})
				.catch((error: Error) => {
					// if mutation fails (e.g. there is another archival operation
					// running in the BE), retry two times before throwing an error
					// It retries after 3 seconds, if it sees an exception again,
					// try another time after 5 seconds

					// TODO: need to cover the case for 'page is already archived'
					// In that case we don't need to retry
					if (attemptNumber >= MAX_TRIES) {
						setMutationError(error);

						if (isArchive && isBulk) {
							createAnalyticsEvent({
								type: 'sendOperationalEvent',
								data: {
									action: 'failed',
									actionSubject: 'bulkArchive',
									source: featureName,
									attributes: {
										attemptNumber,
										pageCount,
										errorMessage: error.message,
									},
								},
							}).fire();
						}

						experienceTracker.stopOnError({
							name: getExperienceName(isArchive),
							error,
							attributes: {
								featureName,
							},
						});
					} else {
						setTimeout(() => {
							createAnalyticsEvent({
								type: 'sendTrackEvent',
								data: {
									action: `retry${isArchive ? 'Archive' : 'Restore'}Mutation`,
									actionSubject: 'page',
									actionSubjectId: isBulk ? 'multiple' : contentId,
									source: analyticsSource,
									attributes: {
										attemptNumber,
										pageCount,
									},
								},
							}).fire();

							tryArchiveMutation(variables, attemptNumber === 1 ? 3000 : 5000, attemptNumber);
						}, waitTimeBeforeRetry);
					}
				});
		},
		[
			analyticsSource,
			contentId,
			createAnalyticsEvent,
			experienceTracker,
			featureName,
			isArchive,
			isBulk,
			mutateStartPageArchival,
			pageCount,
		],
	);

	const startMutation = useCallback(
		(
			variables: TMutationVariables,
			trackEventAttributes?: object,
			shouldUseAutomationDiscovery = false,
		) => {
			if (!shouldShowProgress) {
				setShouldShowProgress(true);
				setShouldUseAutomationDiscovery(shouldUseAutomationDiscovery);
				setIsArchiveInProgress(true);
				additionalTrackEventAttributesRef.current = trackEventAttributes;
				fireStartArchivalEvent(trackEventAttributes);
				tryArchiveMutation(variables);
			}
		},
		[
			additionalTrackEventAttributesRef,
			fireStartArchivalEvent,
			shouldShowProgress,
			tryArchiveMutation,
		],
	);

	const getSuccessLink = () => {
		if (isArchive) {
			return SPACE_ARCHIVED_PAGES.toUrl({ spaceKey: spaceAlias });
		} else {
			// Restore
			return webuiLink ? `${CONTEXT_PATH}${webuiLink}` : `${CONTEXT_PATH}`;
		}
	};

	const handleSuccess = () => {
		setIsArchiveInProgress(false);

		if (onSuccess) {
			onSuccess();
		}
	};

	// Get the appropriate message strings for the archive action
	const flagI18n = useMemo(() => getArchiveFlagI18nWithErrorMsgs({ isArchive }), [isArchive]);

	return (
		<Fragment>
			{shouldShowProgress ? (
				<ArchiveProgressReport
					flagI18n={flagI18n}
					longTaskId={longTaskId}
					onSuccess={handleSuccess}
					isArchive={isArchive}
					successLinkHref={getSuccessLink()}
					contentId={contentId}
					source={source}
					error={mutationError}
					isBulk={isBulk}
					onSuccessLinkClick={onSuccessLinkClick}
					pageCount={pageCount}
					spaceId={spaceId}
					additionalTrackEventAttributes={additionalTrackEventAttributesRef.current}
					shouldUseAutomationDiscovery={shouldUseAutomationDiscovery}
					featureName={featureName}
				/>
			) : null}
			{/* TriggerComponent both triggers startMutation (on user action) and
          receives data from this component about the final end status of the
          attempt via archivingError and isArchiveInProgress props */}
			<TriggerComponent
				archivingError={mutationError}
				isArchiveInProgress={isArchiveInProgress}
				analyticsSource={analyticsSource}
				isArchive={isArchive}
				source={source}
				spaceAlias={spaceAlias}
				startMutationCallback={startMutation}
			/>
		</Fragment>
	);
}
